/**
 * Phase 2 Wave 1 (Task B) — per-entity tessellated vertex buffer cache.
 *
 * # Problem
 *
 * Every `bulkLoad` walks the renderable list and tessellates curved
 * geometry (arc / circle / ellipse / spline / hatch arc-edge /
 * dimension arc) into line-pair vertex arrays. The output is a
 * `Float32Array` of fixed size per entity — entirely deterministic
 * given the entity's geometry fields. On large drawings these
 * tessellation passes dominate bulkLoad latency, and the result is
 * thrown away each time we rebuild meshes (style toggles, selection
 * changes, layer visibility flips all re-run bulkLoad).
 *
 * # Design
 *
 * Sidecar `Map<entityId, { version, buffer }>`. Callers wrap their
 * tessellator function with `getOrTessellate(id, version, () => ...)`.
 * If the cache has an entry with the same version we return the
 * existing `Float32Array`; otherwise we run the tessellator, store the
 * result, and return it.
 *
 * # Version contract
 *
 * `version` is a monotonic per-entity counter the caller controls.
 * Style-only changes (layer/color/linetype) MUST NOT bump it; geometry
 * field changes (start/end/center/radius/control points/...) MUST bump
 * it. Track 1 / PR-V2's `EntityMetadata.geomRev` field is the intended
 * source of truth; until that field is wired through the dispatcher
 * callers can pass a synthetic version derived from the geometry hash
 * or document indexVersion as a coarse fallback.
 *
 * # Invalidation
 *
 * - `invalidate(id)` drops a single entity (used on explicit geometry
 *   commits and on entity removal).
 * - `clear()` flushes the entire cache (used on document import / hot
 *   reload / unit-test teardown).
 *
 * # Memory bound
 *
 * No hard cap — the cache scales with active entity count. Each entry
 * holds one Float32Array; the renderer's bulkLoad walks every entity
 * once, so the steady-state cache footprint matches the live drawing's
 * line-vertex payload. Cache eviction can be added in a follow-up if
 * monitoring shows leaks across re-imports.
 *
 * # AutoCAD parity
 *
 * Internal optimization — pure passthrough on cache miss. Cache hits
 * return an identical Float32Array. No behavior change.
 */

/** Sidecar entry. `buffer` is the cached tessellation output; `version`
 *  is the caller's per-entity geometry revision. */
interface TessellationEntry {
  version: number;
  buffer: Float32Array;
}

const cache = new Map<string, TessellationEntry>();
const stats = { hits: 0, misses: 0 };

/**
 * Acquire or compute the tessellated vertex buffer for `entityId`.
 *
 * On cache hit (`entry.version === version`) returns the stored buffer
 * directly — caller MUST NOT mutate it in place (treat as immutable).
 *
 * On cache miss (entry absent OR stale version) runs `tessellator`,
 * stores the result, and returns it. The tessellator MAY be called
 * concurrently for different ids; the cache is single-keyed so no
 * cross-entity races exist.
 */
export function getOrTessellate(
  entityId: string,
  version: number,
  tessellator: () => Float32Array,
): Float32Array {
  const existing = cache.get(entityId);
  if (existing && existing.version === version) {
    stats.hits += 1;
    return existing.buffer;
  }
  const buffer = tessellator();
  cache.set(entityId, { version, buffer });
  stats.misses += 1;
  return buffer;
}

/**
 * Drop the cached tessellation for a single entity. Used by the
 * commit-mutation hook when the entity's geometry fields change (or
 * when the entity itself is removed).
 */
export function invalidate(entityId: string): void {
  cache.delete(entityId);
}

/** Drop every cached entry. Hot-reload / document-import escape hatch. */
export function clear(): void {
  cache.clear();
  stats.hits = 0;
  stats.misses = 0;
}

/** Cache observability for telemetry + diagnostic panels. */
export interface CacheStats {
  /** Lifetime hit count. */
  hits: number;
  /** Lifetime miss count (= tessellator invocation count). */
  misses: number;
  /** Current entry count. */
  size: number;
  /** Total byte size of cached buffers (Float32Array.byteLength sum). */
  bytes: number;
}

export function getStats(): CacheStats {
  let bytes = 0;
  for (const entry of cache.values()) {
    bytes += entry.buffer.byteLength;
  }
  return {
    hits: stats.hits,
    misses: stats.misses,
    size: cache.size,
    bytes,
  };
}
